// TODO: From App New
<template>
  <div>
    <!-- Read-only mode indicator -->
    <b-alert
      v-if="isSaleOngoing"
      show
      :variant="alertVariant"
    >
      <feather-icon
        icon="InfoIcon"
        class="mr-50"
      />
      {{ readOnlyMessage }}
    </b-alert>

    <div class="form-row align-items-center mb-2">
      <span class="col-auto">Tambah Produk:</span>

      <div class="col-md">
        <v-select
          ref="productSelect"
          v-model="selectedOption"
          label="name"
          class="w-fill-available"
          :options="searchOptions"
          :loading="loadingSearch || loadingAddProduct"
          :disabled="disabled || loadingAddProduct"
          :clearable="false"
          :searchable="true"
          :filterable="false"
          placeholder="Ketik untuk mencari produk..."
          @option:selected="handleProductSelected"
          @search="handleSearch"
          @open="handleDropdownOpen"
        >
          <template #option="item">
            <div class="d-flex align-items-center text-truncate">
              <div class="flex-grow-0">
                <img
                  :src="item.images[0].image_url | thumbnail(40)"
                  alt="Product Image"
                  width="40px"
                  height="40px"
                  class="rounded border mr-75"
                >
              </div>
              <div class="flex-grow-1 text-truncate">
                <div class="text-truncate">
                  {{ item.name }}
                </div>
              </div>
            </div>
          </template>
          <!-- No options -->
          <template #no-options="{ search }">
            <span v-if="search && !loadingSearch">Tidak ada hasil untuk "{{ search }}".</span>
            <span v-else>Ketik untuk mencari...</span>
          </template>
          <!-- Custom spinner -->
          <template #spinner="{ loading }">
            <b-spinner
              v-if="loading"
              variant="primary"
              small
            />
          </template>
        </v-select>
      </div>

      <div class="col-auto">
        <b-button
          variant="outline-primary"
          :disabled="disabled || isSaleOngoing"
          @click="showBulkAddModal = true"
        >
          <feather-icon
            icon="CheckSquareIcon"
            class="mr-50"
          />
          Tambah Masal
        </b-button>
      </div>
    </div>

    <!-- Modal Tambah Masal -->
    <b-modal
      v-model="showBulkAddModal"
      title="Tambah Produk Masal"
      centered
      cancel-variant="outline-secondary"
      cancel-title="Batal"
      :ok-disabled="isProcessing || isSubmitting"
      :cancel-disabled="isProcessing || isSubmitting"
      :no-close-on-backdrop="isProcessing || isSubmitting"
      :no-close-on-esc="isProcessing || isSubmitting"
      @ok="handleBulkAddOk"
      @hidden="handleBulkAddHidden"
    >
      <template #modal-ok>
        <div class="position-relative d-flex align-items-center justify-content-center">
          <span :class="{ 'invisible': isProcessing || isSubmitting }">
            {{ isCheckingItems ? 'Tambah Terpilih' : 'Proses Pencarian' }}
          </span>
          <b-spinner
            v-if="isProcessing || isSubmitting"
            small
            class="position-absolute"
          />
        </div>
      </template>

      <!-- Textarea Input View -->
      <div v-if="!isCheckingItems">
        <b-form-group
          label="Daftar SKU / ID Produk"
          description="Paste daftar SKU produk dari Excel ke sini. Tiap baris akan diproses sebagai satu SKU."
        >
          <b-form-textarea
            v-model="bulkInputText"
            placeholder="Tuliskan/Paste SKU di sini..."
            rows="10"
          />
        </b-form-group>
      </div>

      <!-- Staging List View -->
      <div v-else>
        <div class="mb-1">
          <p class="mb-50 text-muted">
            Memproses {{ processedCount }} dari {{ stagingItems.length }} produk...
          </p>
          <b-progress
            :value="processedCount"
            :max="stagingItems.length"
            variant="primary"
            striped
            animated
          />
        </div>
        <div
          class="staging-list-container"
          style="max-height: 400px; overflow-y: auto;"
        >
          <b-card
            v-for="item in stagingItems"
            :key="item.id"
            no-body
            class="mb-1 shadow-none"
            :class="{
              'border border-danger': item.status === 'failed',
              'bg-light-danger': item.status === 'failed',
              'border': item.status !== 'failed'
            }"
          >
            <b-card-body class="p-1 d-flex align-items-start">
              <!-- Left: Avatar -->
              <div class="mr-1">
                <b-avatar
                  v-if="item.status === 'success'"
                  :src="item.imageUrl"
                  :text="item.productName ? item.productName.charAt(0).toUpperCase() : ''"
                  size="50px"
                  variant="light-primary"
                  rounded="lg"
                  class="border"
                />
                <b-avatar
                  v-else-if="item.status === 'failed'"
                  size="50px"
                  variant="light-danger"
                  rounded="lg"
                  class="border border-danger"
                >
                  <feather-icon
                    icon="XIcon"
                    size="20"
                    class="text-danger"
                  />
                </b-avatar>
                <b-avatar
                  v-else
                  size="50px"
                  variant="light-secondary"
                  rounded="lg"
                  class="border"
                >
                  <b-spinner
                    v-if="item.status === 'loading'"
                    small
                  />
                  <feather-icon
                    v-else
                    icon="ImageIcon"
                    size="20"
                  />
                </b-avatar>
              </div>

              <!-- Middle: Text Content -->
              <div class="flex-grow-1 overflow-hidden pr-1">
                <!-- If Loading -->
                <div v-if="item.status === 'loading'">
                  <div
                    class="font-weight-bolder text-muted"
                    style="font-size: 0.95rem; word-break: break-word;"
                  >
                    Mencari Produk...
                  </div>
                  <div
                    class="text-muted"
                    style="font-size: 0.85rem; word-break: break-word;"
                  >
                    {{ item.originalText }}
                  </div>
                </div>

                <!-- If Success -->
                <div v-else-if="item.status === 'success'">
                  <div
                    class="font-weight-bolder"
                    style="font-size: 0.95rem; display: -webkit-box; -webkit-line-clamp: 2; line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;"
                  >
                    {{ item.productName }}
                  </div>
                  <div
                    class="text-muted mt-25"
                    style="font-size: 0.85rem; word-break: break-word;"
                  >
                    {{ item.originalText }}
                  </div>
                </div>

                <!-- If Failed -->
                <div v-else-if="item.status === 'failed'">
                  <div
                    class="font-weight-bolder text-danger"
                    style="font-size: 0.95rem;"
                  >
                    Produk tidak ditemukan
                  </div>
                  <div
                    class="text-danger mt-25"
                    style="font-size: 0.85rem; word-break: break-word;"
                  >
                    {{ item.originalText }}
                  </div>
                </div>

                <!-- If Pending -->
                <div v-else>
                  <div
                    class="font-weight-bolder text-muted"
                    style="font-size: 0.95rem; word-break: break-word;"
                  >
                    Menunggu Giliran...
                  </div>
                  <div
                    class="text-muted mt-25"
                    style="font-size: 0.85rem; word-break: break-word;"
                  >
                    {{ item.originalText }}
                  </div>
                </div>
              </div>

              <!-- Right: Action / Checkbox -->
              <div class="flex-shrink-0 d-flex align-items-center">
                <b-form-checkbox
                  v-if="item.status === 'success'"
                  v-model="item.selected"
                />
              </div>
            </b-card-body>
          </b-card>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BSpinner, BAlert, BButton, BModal, BFormGroup, BFormTextarea,
  BCard, BCardBody, BProgress, BAvatar, BFormCheckbox,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import debounce from 'lodash/debounce'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'ProductSearchBar',
  components: {
    BSpinner,
    BAlert,
    BButton,
    BModal,
    BFormGroup,
    BFormTextarea,
    BCard,
    BCardBody,
    BAvatar,
    BFormCheckbox,
    BProgress,
    vSelect,
  },
  props: {
    brandSlug: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loadingAddProduct: {
      type: Boolean,
      default: false,
    },
    isSaleOngoing: {
      type: Boolean,
      default: false,
    },
    alertVariant: {
      type: String,
      default: 'info',
    },
    readOnlyMessage: {
      type: String,
      default: 'Pengaturan harga tidak dapat diubah.',
    },
  },
  emits: ['product-selected'],
  data() {
    return {
      selectedOption: null,
      searchOptions: [],
      loadingSearch: false,
      searchText: '',
      showBulkAddModal: false,
      bulkInputText: '',
      isCheckingItems: false,
      stagingItems: [],
      processedCount: 0,
      isProcessing: false,
      isSubmitting: false,
    }
  },
  methods: {
    handleProductSelected(item) {
      this.$emit('product-selected', item)
      this.selectedOption = null
    },

    handleSearch: debounce(function search(input, setLoading) {
      this.searchText = input

      if (!input) {
        return
      }
      this.loadingSearch = true
      setLoading(true)

      const searchParams = {
        keyword: input,
        limit: 10,
        page: 1,
      }

      if (this.brandSlug) {
        searchParams['filter[merchant_slug]'] = this.brandSlug
      }
      this.$store.dispatch('saleV2/searchProducts', searchParams).then(data => {
        this.searchOptions = data
      })
        .finally(() => {
          this.loadingSearch = false
          setLoading(false)
        })
    }, 500),

    handleDropdownOpen() {
      // When dropdown opens, if we have stored search text, restore it
      if (this.searchText && this.$refs.productSelect) {
        this.$nextTick(() => {
          const searchInput = this.$refs.productSelect.$el.querySelector('.vs__search')
          if (searchInput) {
            searchInput.value = this.searchText
            searchInput.focus()
          }
        })
      }
    },

    async handleBulkAddOk(bvModalEvent) {
      if (!this.isCheckingItems) {
        // Prevent modal from closing
        bvModalEvent.preventDefault()

        const textArray = this.bulkInputText.split('\n').filter(text => text.trim() !== '')

        if (textArray.length > 0) {
          // Switch to staging list view
          this.isCheckingItems = true
          this.stagingItems = textArray.map((text, index) => ({
            id: `temp-${index}`,
            originalText: text,
            status: 'pending',
          }))

          await this.processItems()
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Peringatan',
              icon: 'AlertCircleIcon',
              text: 'Harap masukkan setidaknya satu SKU produk!',
              variant: 'warning',
            },
          })
        }
      } else {
        // Prevent modal from closing immediately
        bvModalEvent.preventDefault()

        // Submit the checked items to the parent
        const selectedProducts = this.stagingItems.filter(item => item.status === 'success' && item.selected && item.rawProduct)
        if (selectedProducts.length > 0) {
          this.isSubmitting = true

          for (let i = 0; i < selectedProducts.length; i += 1) {
            const item = selectedProducts[i]
            // Parameter kedua 'false' agar SaleSetup tidak memunculkan toast satu-satu
            this.$emit('product-selected', item.rawProduct, false)
            // Jeda 300ms untuk efek loading Submit Terpilih yang alami
            // eslint-disable-next-line no-await-in-loop
            await new Promise(resolve => setTimeout(resolve, 300))
          }

          this.isSubmitting = false

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Produk Ditambahkan',
              icon: 'CheckIcon',
              text: `Berhasil menambahkan ${selectedProducts.length} produk ke dalam promo.`,
              variant: 'success',
            },
          })

          this.$nextTick(() => {
            this.showBulkAddModal = false
          })
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Peringatan',
              icon: 'AlertCircleIcon',
              text: 'Harap pilih setidaknya satu produk yang berhasil ditemukan!',
              variant: 'warning',
            },
          })
        }
      }
    },

    async processItems() {
      this.isProcessing = true
      this.processedCount = 0

      // Reset all items to pending before loop
      this.stagingItems = this.stagingItems.map(item => ({
        ...item,
        status: 'pending',
      }))

      for (let i = 0; i < this.stagingItems.length; i += 1) {
        const item = this.stagingItems[i]

        // Start loading for current item
        const loadingTarget = this.stagingItems.find(x => x.id === item.id)
        if (loadingTarget) {
          loadingTarget.status = 'loading'
        }

        // Hit actual API
        try {
          const searchParams = {
            limit: 1,
            page: 1,
            'filter[sku]': item.originalText.trim(),
          }

          if (this.brandSlug) {
            searchParams['filter[merchant_slug]'] = this.brandSlug
          }

          // eslint-disable-next-line no-await-in-loop
          const data = await this.$store.dispatch('saleV2/searchProducts', searchParams)

          // Check if item still exists (hasn't been deleted by user)
          const target = this.stagingItems.find(x => x.id === item.id)
          if (target) {
            if (data && data.length > 0) {
              const product = data[0]
              target.status = 'success'
              target.productName = product.name
              target.imageUrl = product.images && product.images.length > 0 ? product.images[0].image_url : null
              target.selected = true
              target.rawProduct = product
            } else {
              target.status = 'failed'
            }
          }
        } catch (error) {
          const target = this.stagingItems.find(x => x.id === item.id)
          if (target) target.status = 'failed'
        }

        this.processedCount += 1
      }
      this.isProcessing = false
    },

    removeItem(id) {
      this.stagingItems = this.stagingItems.filter(item => item.id !== id)
    },

    handleBulkAddHidden() {
      // Reset properties when modal is closed
      this.bulkInputText = ''
      this.isCheckingItems = false
      this.stagingItems = []
      this.processedCount = 0
      this.isProcessing = false
      this.isSubmitting = false
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.cursor-pointer {
  cursor: pointer;
}

/* Ensure checkbox and its label inside staging list use pointer cursor */
.staging-list-container {
  .custom-control-label,
  .custom-control-input {
    cursor: pointer !important;
  }
}
</style>
